<script setup>
import { computed, watch } from "vue";
import { useField } from "vee-validate";
import * as yup from "yup";

const props = defineProps({
  name: String,
  label: String,
  modelValue: String,
  placeholder: String,
  description: String,
  max: Number,
  rows: {
    type: Number,
    default: 6,
  },
  showRequired: {
    type: Boolean,
    default: true, // Show required asterisk by default
  },
  required: Boolean,
});

const emit = defineEmits(["update:modelValue"]);

const validationRules = computed(() => {
  let schema = yup
    .string()
    .max(props.max || Infinity, props.max ? `${props.label} is too long.` : "");

  if (props.required) {
    schema = schema.trim().required(`${props.label} is required.`);
  }
  return schema;
});

// Vee-validate field setup
const { value, errorMessage, validate, setValue, meta } = useField(
  props.name,
  validationRules,
  {
    initialValue: props.modelValue,
  }
);
defineExpose({ validate, meta });

watch(value, (newValue) => {
  emit("update:modelValue", newValue);
});

watch(
  () => props.modelValue,
  (newModelValue) => {
    if (newModelValue !== value.value) {
      setValue(newModelValue);
    }
  }
);
</script>

<template>
  <fieldset class="frm-field">
    <label :for="name" class="block mb-10">
      {{ label }}
      <span v-if="required && showRequired" class="text-red-500">*</span>
    </label>
    <div class="frm-input frm-textarea py-15">
      <textarea
        :id="name"
        :name="name"
        :rows="rows"
        :placeholder="placeholder"
        v-model="value"
        @blur="validate"
        class="w-full"></textarea>
    </div>
    <span
      v-if="errorMessage"
      class="block mt-10 text-14 text-error leading-none">
      {{ errorMessage }}
    </span>

    <span v-if="description" class="text-13 frm-desc">
      {{ description }}
    </span>
  </fieldset>
</template>

<style scoped lang="scss">
/* .frm-input is pill-shaped for single-line inputs */
.frm-textarea {
  border-radius: 1.5rem;

  &:has(textarea:focus) {
    outline: 2px solid var(--blue, #189fce);
    outline-offset: -1px;
  }

  textarea {
    resize: vertical;
    outline: none;
  }
}
</style>
